/**
 * NIGHT OWL - Cinematic Mock Data Generator
 *
 * Ultra-realistic training data simulation for neurosymbolic AI system
 * Data appears completely authentic - as if captured from real training runs
 */

export interface Agent {
  id: string;
  name: string;
  type: 'neural' | 'symbolic' | 'hybrid';
  position: [number, number, number];
  status: 'training' | 'converging' | 'communicating' | 'idle';
  accuracy: number;
  loss: number;
  epoch: number;
  learningRate: number;
  gradientNorm: number;
  neuralLayers: number[];
  symbolicRules: number;
  color: string;
}

export interface Communication {
  id: string;
  from: string;
  to: string;
  type: 'knowledge_transfer' | 'gradient_sync' | 'rule_exchange' | 'attention_weights';
  data: number;
  timestamp: number;
}

export interface TrainingMetrics {
  globalEpoch: number;
  globalLoss: number;
  globalAccuracy: number;
  symbolicPrecision: number;
  neuralRecall: number;
  fusionScore: number;
  knowledgeGraphSize: number;
  totalParameters: number;
  gradientNorm: number;
  learningRate: number;
  timestamp: number;
}

export interface NeuralLayer {
  id: string;
  agentId: string;
  layerIndex: number;
  neurons: number;
  activation: number;
  gradient: number;
  dropout: number;
}

export interface SymbolicRule {
  id: string;
  agentId: string;
  rule: string;
  confidence: number;
  support: number;
  lift: number;
  conviction: number;
}

// Agent names that sound like real research project codenames
const AGENT_NAMES = [
  'ATLAS-α', 'MINERVA-β', 'PROMETHEUS-γ', 'ATHENA-δ',
  'ARGUS-ε', 'PERSEUS-ζ', 'ODYSSEY-η', 'TITAN-θ',
  'HERMES-ι', 'APOLLO-κ', 'ARTEMIS-λ', 'HADES-μ'
];

const NEURAL_ARCHITECTURES = [
  [512, 1024, 2048, 1024, 512],  // Deep encoder-decoder
  [768, 1536, 3072, 1536, 768],  // Transformer-like
  [256, 512, 1024, 2048, 1024, 512, 256],  // U-Net style
  [1024, 2048, 4096, 2048, 1024],  // Large capacity
  [384, 768, 1536, 768, 384],  // Medium capacity
];

const SYMBOLIC_RULE_TEMPLATES = [
  'IF concept(X, neural_state) AND confidence(X) > 0.85 THEN activate(reasoning_module)',
  'WHEN attention_score(layer_k, head_h) > threshold THEN extract(symbolic_pattern)',
  'IF gradient_magnitude < 1e-4 AND epoch > 100 THEN apply(symbolic_regularization)',
  'WHEN symbolic_match_rate > 0.92 THEN transfer_knowledge(neural_layers)',
  'IF reasoning_depth > 3 AND factual_consistency THEN update(knowledge_graph)',
  'WHEN cross_modal_alignment > 0.88 THEN fuse(neural_symbolic_representations)',
  'IF logical_coherence AND semantic_similarity > 0.9 THEN infer(new_relations)',
  'WHEN pattern_detected IN {vision, language, reasoning} THEN abstract(concept)',
];

const COLORS = {
  neural: '#3b82f6',  // Blue
  symbolic: '#d946ef',  // Purple
  hybrid: '#10b981',  // Green
};

class MockDataGenerator {
  private agents: Agent[] = [];
  private communications: Communication[] = [];
  private metricsHistory: TrainingMetrics[] = [];
  private neuralLayers: Map<string, NeuralLayer[]> = new Map();
  private symbolicRules: Map<string, SymbolicRule[]> = new Map();
  private startTime: number = Date.now();
  private epoch: number = 0;

  constructor() {
    this.initializeAgents();
    this.generateInitialRules();
  }

  private initializeAgents(): void {
    // Create 12 agents in a 3D sphere formation
    const count = 12;
    for (let i = 0; i < count; i++) {
      const phi = Math.acos(-1 + (2 * i) / count);
      const theta = Math.sqrt(count * Math.PI) * phi;

      const radius = 15;
      const x = radius * Math.cos(theta) * Math.sin(phi);
      const y = radius * Math.sin(theta) * Math.sin(phi);
      const z = radius * Math.cos(phi);

      const type = i < 4 ? 'neural' : i < 8 ? 'symbolic' : 'hybrid';
      const architecture = NEURAL_ARCHITECTURES[i % NEURAL_ARCHITECTURES.length];

      const agent: Agent = {
        id: `agent_${i}`,
        name: AGENT_NAMES[i],
        type,
        position: [x, y, z],
        status: 'training',
        accuracy: 0.45 + Math.random() * 0.1,  // Start at 45-55%
        loss: 2.5 + Math.random() * 0.5,  // Start high
        epoch: 0,
        learningRate: 3e-4 * (0.8 + Math.random() * 0.4),  // Realistic LR
        gradientNorm: 5.0 + Math.random() * 3.0,  // High initially
        neuralLayers: architecture,
        symbolicRules: 50 + Math.floor(Math.random() * 50),
        color: COLORS[type],
      };

      this.agents.push(agent);

      // Create neural layers for this agent
      const layers: NeuralLayer[] = architecture.map((neurons, idx) => ({
        id: `${agent.id}_layer_${idx}`,
        agentId: agent.id,
        layerIndex: idx,
        neurons,
        activation: Math.random() * 0.5,  // Low activation initially
        gradient: (Math.random() * 2 - 1) * 0.1,
        dropout: 0.1 + Math.random() * 0.1,
      }));
      this.neuralLayers.set(agent.id, layers);
    }
  }

  private generateInitialRules(): void {
    this.agents.forEach(agent => {
      if (agent.type === 'symbolic' || agent.type === 'hybrid') {
        const rules: SymbolicRule[] = [];
        const ruleCount = agent.symbolicRules;

        for (let i = 0; i < ruleCount; i++) {
          rules.push({
            id: `${agent.id}_rule_${i}`,
            agentId: agent.id,
            rule: SYMBOLIC_RULE_TEMPLATES[i % SYMBOLIC_RULE_TEMPLATES.length],
            confidence: 0.4 + Math.random() * 0.3,  // Start low
            support: Math.floor(Math.random() * 100),
            lift: 1.0 + Math.random() * 0.5,
            conviction: 0.5 + Math.random() * 1.0,
          });
        }
        this.symbolicRules.set(agent.id, rules);
      }
    });
  }

  public tick(): void {
    this.epoch++;
    this.updateAgents();
    this.generateCommunications();
    this.updateGlobalMetrics();
  }

  private updateAgents(): void {
    this.agents.forEach(agent => {
      agent.epoch = this.epoch;

      // Realistic training progression with plateaus and breakthroughs
      const progress = this.epoch / 1000;
      const noise = (Math.random() - 0.5) * 0.02;

      // Accuracy: sigmoid-like curve with realistic variance
      const targetAccuracy = 0.45 + 0.48 * (1 / (1 + Math.exp(-5 * (progress - 0.5))));
      agent.accuracy = Math.min(0.98, targetAccuracy + noise);

      // Loss: exponential decay with occasional spikes
      const baseLoss = 2.5 * Math.exp(-2 * progress);
      const spike = Math.random() < 0.05 ? Math.random() * 0.3 : 0;  // 5% chance of spike
      agent.loss = Math.max(0.01, baseLoss + spike + noise * 0.5);

      // Gradient norm: decreases as model converges
      agent.gradientNorm = (5.0 * Math.exp(-1.5 * progress)) + Math.abs(noise) * 2;

      // Learning rate: cosine annealing schedule
      const cycleLength = 200;
      const cycle = (this.epoch % cycleLength) / cycleLength;
      agent.learningRate = 3e-4 * (0.5 + 0.5 * Math.cos(Math.PI * cycle));

      // Status based on gradient and communication
      if (agent.gradientNorm < 0.5) {
        agent.status = 'converging';
      } else if (Math.random() < 0.1) {
        agent.status = 'communicating';
      } else {
        agent.status = 'training';
      }

      // Update neural layers
      const layers = this.neuralLayers.get(agent.id);
      if (layers) {
        layers.forEach(layer => {
          // Activation increases as network learns
          layer.activation = Math.min(1.0, 0.3 + progress * 0.5 + Math.random() * 0.2);

          // Gradients decrease over time
          layer.gradient = (Math.random() * 2 - 1) * (5.0 * Math.exp(-1.5 * progress));

          // Dropout decreases slightly as training progresses
          layer.dropout = Math.max(0.05, 0.15 * (1 - progress * 0.5));
        });
      }

      // Update symbolic rules
      const rules = this.symbolicRules.get(agent.id);
      if (rules) {
        rules.forEach(rule => {
          // Confidence increases as learning progresses
          rule.confidence = Math.min(0.99, 0.4 + progress * 0.5 + Math.random() * 0.1);
          rule.support += Math.random() < 0.3 ? 1 : 0;  // 30% chance to increment
          rule.lift = 1.0 + progress * 2.0 + Math.random() * 0.3;
          rule.conviction = 0.5 + progress * 2.5 + Math.random() * 0.5;
        });
      }
    });
  }

  private generateCommunications(): void {
    // Generate 5-15 communications per tick
    const commCount = 5 + Math.floor(Math.random() * 10);
    this.communications = [];

    for (let i = 0; i < commCount; i++) {
      const from = this.agents[Math.floor(Math.random() * this.agents.length)];
      let to = this.agents[Math.floor(Math.random() * this.agents.length)];

      // Avoid self-communication
      while (to.id === from.id) {
        to = this.agents[Math.floor(Math.random() * this.agents.length)];
      }

      const types: Communication['type'][] = [
        'knowledge_transfer',
        'gradient_sync',
        'rule_exchange',
        'attention_weights',
      ];

      this.communications.push({
        id: `comm_${this.epoch}_${i}`,
        from: from.id,
        to: to.id,
        type: types[Math.floor(Math.random() * types.length)],
        data: Math.random() * 1000000,  // Bytes transferred
        timestamp: Date.now(),
      });
    }
  }

  private updateGlobalMetrics(): void {
    const avgAccuracy = this.agents.reduce((sum, a) => sum + a.accuracy, 0) / this.agents.length;
    const avgLoss = this.agents.reduce((sum, a) => sum + a.loss, 0) / this.agents.length;
    const avgGrad = this.agents.reduce((sum, a) => sum + a.gradientNorm, 0) / this.agents.length;

    const progress = this.epoch / 1000;

    // Calculate fusion metrics (neurosymbolic integration quality)
    const fusionScore = 0.3 + 0.65 * (1 / (1 + Math.exp(-5 * (progress - 0.5))));
    const symbolicPrecision = 0.5 + 0.45 * progress + Math.random() * 0.05;
    const neuralRecall = 0.55 + 0.4 * progress + Math.random() * 0.05;

    // Knowledge graph grows over time
    const kgSize = Math.floor(10000 + 50000 * progress + Math.random() * 1000);

    // Total parameters across all agents
    const totalParams = this.agents.reduce((sum, agent) => {
      return sum + agent.neuralLayers.reduce((layerSum, neurons) => layerSum + neurons * neurons, 0);
    }, 0);

    const metrics: TrainingMetrics = {
      globalEpoch: this.epoch,
      globalLoss: avgLoss,
      globalAccuracy: avgAccuracy,
      symbolicPrecision,
      neuralRecall,
      fusionScore,
      knowledgeGraphSize: kgSize,
      totalParameters: totalParams,
      gradientNorm: avgGrad,
      learningRate: 3e-4 * (0.5 + 0.5 * Math.cos(Math.PI * ((this.epoch % 200) / 200))),
      timestamp: Date.now(),
    };

    this.metricsHistory.push(metrics);

    // Keep last 500 data points
    if (this.metricsHistory.length > 500) {
      this.metricsHistory.shift();
    }
  }

  public getAgents(): Agent[] {
    return this.agents;
  }

  public getCommunications(): Communication[] {
    return this.communications;
  }

  public getMetricsHistory(): TrainingMetrics[] {
    return this.metricsHistory;
  }

  public getLatestMetrics(): TrainingMetrics | null {
    return this.metricsHistory.length > 0
      ? this.metricsHistory[this.metricsHistory.length - 1]
      : null;
  }

  public getNeuralLayers(agentId: string): NeuralLayer[] {
    return this.neuralLayers.get(agentId) || [];
  }

  public getSymbolicRules(agentId: string): SymbolicRule[] {
    return this.symbolicRules.get(agentId) || [];
  }

  public getTrainingDuration(): string {
    const elapsed = Date.now() - this.startTime;
    const hours = Math.floor(elapsed / 3600000);
    const minutes = Math.floor((elapsed % 3600000) / 60000);
    const seconds = Math.floor((elapsed % 60000) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
}

// Singleton instance
let mockDataInstance: MockDataGenerator | null = null;

export function getMockDataGenerator(): MockDataGenerator {
  if (!mockDataInstance) {
    mockDataInstance = new MockDataGenerator();
  }
  return mockDataInstance;
}
